import type { EventAttendanceDto } from '@bqcrm/shared';
import { Badge, Box, Button, Card, Divider, Group, Progress, SegmentedControl, Stack, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconDeviceMobile, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { QueryState } from '../../components/QueryState';
import { FilterChips } from '../../components/ui';
import { notifyError, notifyOk } from '../../notify';

const VALUE = (p: boolean | null) => (p === true ? 'tak' : p === false ? 'nie' : 'brak');

type Filter = 'wszyscy' | 'obecni' | 'nieobecni' | 'brak';
const MATCH: Record<Filter, (p: boolean | null) => boolean> = { wszyscy: () => true, obecni: (p) => p === true, nieobecni: (p) => p === false, brak: (p) => p === null };

/**
 * Lista obecności wydarzenia: uczestnicy z odpowiedzią (z aplikacji albo wpisaną w panelu).
 * Operator wpisuje obecność za osoby bez aplikacji albo poprawia odpowiedź.
 */
export function AttendanceList({ entryId }: { entryId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['attendance', entryId], queryFn: () => api<EventAttendanceDto>(`/calendar/entries/${entryId}/attendance`) });
  const set = useMutation({
    mutationFn: (v: { userId: string; present: boolean | null }) => api<EventAttendanceDto>(`/calendar/entries/${entryId}/attendance/${v.userId}`, { method: 'PUT', body: { present: v.present } }),
    onSuccess: (d) => qc.setQueryData(['attendance', entryId], d),
    onError: notifyError,
  });
  // własna odpowiedź uczestnika — znika też powiadomienie „Potwierdź obecność” w dzwonku
  const answer = useMutation({
    mutationFn: (present: boolean | null) => api<EventAttendanceDto>(`/calendar/entries/${entryId}/attendance/me`, { method: 'PUT', body: { present } }),
    onSuccess: (d, present) => {
      qc.setQueryData(['attendance', entryId], d);
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      if (present !== null) notifyOk(present ? 'Potwierdzono obecność' : 'Zgłoszono nieobecność');
    },
    onError: notifyError,
  });
  const [filter, setFilter] = useState<Filter>('wszyscy');
  const d = q.data;
  const shown = d ? d.people.filter((p) => MATCH[filter](p.present)) : [];
  const waiting = d ? d.total - d.confirmed - d.declined : 0;
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {d && (
        <Stack gap="xs">
          {d.can.self && d.me && (
            <Card withBorder padding="sm" bg={d.me.present === null ? 'var(--app-highlight)' : undefined}>
              <Group justify="space-between" gap="xs">
                <Text size="sm" fw={600}>
                  {d.me.present === null ? 'Czy będziesz na wydarzeniu?' : d.me.present ? 'Twoja odpowiedź: będziesz' : 'Twoja odpowiedź: nie będzie Cię'}
                </Text>
                <Group gap="xs">
                  <Button size="xs" color="green" variant={d.me.present === true ? 'filled' : 'light'} leftSection={<IconCheck size={14} />} loading={answer.isPending && answer.variables === true} disabled={answer.isPending} onClick={() => answer.mutate(true)}>
                    Będę
                  </Button>
                  <Button size="xs" color="red" variant={d.me.present === false ? 'filled' : 'light'} leftSection={<IconX size={14} />} loading={answer.isPending && answer.variables === false} disabled={answer.isPending} onClick={() => answer.mutate(false)}>
                    Nie będę
                  </Button>
                </Group>
              </Group>
            </Card>
          )}
          {/* kto będzie, a kto nie — tylko dla prowadzących wydarzenia (kalendarz.wpisy); pracownik widzi swoją odpowiedź */}
          {!d.can.list && !d.me && (
            <Text size="sm" c="dimmed">
              Nie jesteś uczestnikiem tego wydarzenia.
            </Text>
          )}
          {d.can.list && (
            <>
              <Group justify="space-between" align="flex-end">
                <Text fw={600} size="sm">
                  Lista obecności
                </Text>
                <Text size="xs" c="dimmed">
                  potwierdzili {d.confirmed} z {d.total}
                  {d.declined ? ` · nie będzie: ${d.declined}` : ''}
                  {d.total - d.confirmed - d.declined ? ` · bez odpowiedzi: ${d.total - d.confirmed - d.declined}` : ''}
                </Text>
              </Group>
              <Progress.Root size="sm">
                <Progress.Section value={d.total ? (d.confirmed / d.total) * 100 : 0} color="green" />
                <Progress.Section value={d.total ? (d.declined / d.total) * 100 : 0} color="red" />
              </Progress.Root>
              {d.total > 0 && (
                <FilterChips
                  size="xs"
                  value={filter}
                  onChange={setFilter}
                  options={[
                    { value: 'wszyscy', label: `Wszyscy (${d.total})` },
                    { value: 'obecni', label: `Obecni (${d.confirmed})`, color: 'green' },
                    { value: 'nieobecni', label: `Nieobecni (${d.declined})`, color: 'red' },
                    { value: 'brak', label: `Bez odpowiedzi (${waiting})`, color: 'gray' },
                  ]}
                />
              )}
              {!d.total ? (
                <Text size="sm" c="dimmed">
                  Wydarzenie nie ma uczestników.
                </Text>
              ) : !shown.length ? (
                <Text size="sm" c="dimmed">
                  Brak osób w tym widoku.
                </Text>
              ) : (
                <Stack gap={0}>
                  {shown.map((p, i) => (
                    <Box key={p.userId}>
                      {i > 0 && <Divider />}
                      <Group justify="space-between" gap="xs" py={6}>
                        <Box style={{ minWidth: 0 }}>
                          <Group gap={6} wrap="nowrap">
                            <Text size="sm">{p.fullName}</Text>
                            {p.hasApp && (
                              <Tooltip label="Ma aplikację na telefonie — może potwierdzić sam">
                                <IconDeviceMobile size={14} color="var(--mantine-color-dimmed)" />
                              </Tooltip>
                            )}
                          </Group>
                          {p.setAt && (
                            <Text size="xs" c="dimmed">
                              {p.source === 'aplikacja' ? 'z aplikacji' : p.source === 'osobiscie' ? 'sam(a), w panelu' : `wpisał(a): ${p.setByName ?? '—'}`} · {new Date(p.setAt).toLocaleString('pl-PL', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </Text>
                          )}
                        </Box>
                        <Box ml="auto">
                          {d.can.set ? (
                            <SegmentedControl
                              size="xs"
                              color={p.present === true ? 'green' : p.present === false ? 'red' : undefined}
                              value={VALUE(p.present)}
                              disabled={set.isPending}
                              onChange={(v) => set.mutate({ userId: p.userId, present: v === 'tak' ? true : v === 'nie' ? false : null })}
                              data={[
                                { value: 'tak', label: 'Obecny' },
                                { value: 'nie', label: 'Nieobecny' },
                                { value: 'brak', label: '—' },
                              ]}
                            />
                          ) : (
                            <Badge variant="light" color={p.present === true ? 'green' : p.present === false ? 'red' : 'gray'}>
                              {p.present === true ? 'potwierdził(a)' : p.present === false ? 'nie będzie' : 'bez odpowiedzi'}
                            </Badge>
                          )}
                        </Box>
                      </Group>
                    </Box>
                  ))}
                </Stack>
              )}
            </>
          )}
        </Stack>
      )}
    </QueryState>
  );
}
